/**
 * Token catalogue.
 *
 * The UO platform tracks 16 distinct balance buckets per user. Each bucket is
 * a `DECIMAL(36,18)` column in the `users` table and a matching asset type in
 * the `uo_balance_log` hypertable. Do NOT add or rename tokens here without a
 * coordinated migration + ledger backfill — these literals are referenced by
 * the backend LedgerService, the admin balance-adjust operator form, and the
 * frontend wallet UI.
 *
 * Naming convention mirrors the legacy PHP system so that audit-log exports
 * stay byte-compatible with historical records.
 */

/**
 * Authoritative token identifier. Used as the `asset` discriminator in the
 * ledger (`uo_balance_log.asset`) and as the column name in `users`.
 */
export const TOKEN_IDS = [
  'uo', // 源石 — primary token
  'uoc', // 源石币 — staked / locked UO
  'rp', // 算力积分 (Reward Point)
  'tp', // 贡献积分 (Trading/Task Point)
  'ep', // 能量积分 — EP P2P trading currency
  'ep1', // EP 冻结 1 期
  'ep2', // EP 冻结 2 期
  'iep', // 入金 EP — new-user onboarding allocation
  'sp1', // Split Pool 1
  'sp2', // Split Pool 2
  'sp3', // Split Pool 3
  'mp', // 经理积分 (Manager Point)
  'ap', // 活动积分 (Activity Point)
  'fp', // 分红积分 (Fork/Dividend Point)
  'fc', // 分红币 (Fork Coin)
  'net_weight', // 算力权重 — non-tradable, drives daily settlement share
] as const;

/**
 * Compile-time union of every valid token ID. Any code that accepts a token
 * parameter should type it as `TokenId`, not `string`.
 */
export type TokenId = (typeof TOKEN_IDS)[number];

/**
 * Display metadata for each token. Used by the admin panel balance view and
 * the frontend wallet cards. Display symbol is the short label rendered in
 * tables; display name is the long label shown in detail panels and tooltips.
 */
export const TOKEN_META: Record<TokenId, { symbol: string; name: string; tradable: boolean }> = {
  uo: { symbol: 'UO', name: '源石', tradable: true },
  uoc: { symbol: 'UOC', name: '源石币', tradable: false },
  rp: { symbol: 'RP', name: '算力积分', tradable: false },
  tp: { symbol: 'TP', name: '贡献积分', tradable: false },
  ep: { symbol: 'EP', name: '能量积分', tradable: true },
  ep1: { symbol: 'EP1', name: 'EP一期冻结', tradable: false },
  ep2: { symbol: 'EP2', name: 'EP二期冻结', tradable: false },
  iep: { symbol: 'iEP', name: '入金EP', tradable: false },
  sp1: { symbol: 'SP1', name: '分裂池一', tradable: false },
  sp2: { symbol: 'SP2', name: '分裂池二', tradable: false },
  sp3: { symbol: 'SP3', name: '分裂池三', tradable: false },
  mp: { symbol: 'MP', name: '经理积分', tradable: false },
  ap: { symbol: 'AP', name: '活动积分', tradable: false },
  fp: { symbol: 'FP', name: '分红积分', tradable: false },
  fc: { symbol: 'FC', name: '分红币', tradable: false },
  net_weight: { symbol: 'W', name: '算力权重', tradable: false },
};

/**
 * Subset of tokens that users can transfer peer-to-peer. The EP marketplace
 * only lists `ep`; the OTC module currently supports `uo`. This list exists
 * so the backend can reject transfer requests against non-tradable buckets at
 * the schema layer (see `packages/shared/src/schemas/ledger.ts`).
 */
export const TRADABLE_TOKENS: readonly TokenId[] = TOKEN_IDS.filter((t) => TOKEN_META[t].tradable);

/**
 * Type-guard. Narrows an arbitrary string to `TokenId` — use this at the API
 * boundary before passing the value to the LedgerService.
 */
export function isTokenId(value: string): value is TokenId {
  return (TOKEN_IDS as readonly string[]).includes(value);
}
